import { useMemo, type ReactNode } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBrand } from './brandStore';
import { useResolvedInfo } from './infoPicks';
import { localizeCategoryOrBlank, useCategories } from './catalogCategories';
import { useSubmodels } from './listingStore';
import { useSubmodelFilter } from './submodelFilterStore';
import { displayVariantLabel, variantLabel } from './variantLabel';
import { readProductSize } from './dimensionsRun';
import { formatMeasure, sizeKeyLabel } from './formatUnits';

// WHAT THE PRODUCT IS CALLED — its category and its name, each possibly blank on a manual
// nobody has read yet. One reading, for the two places that say it: the selector's first
// segment (PartsPhase) and the parts table's first column (below).
export function useProductIdentity(docId: string): { category: string; name: string } {
  const { language } = useLanguage();
  const { category } = useBrand(docId);
  const { productName } = useResolvedInfo(docId);
  const cats = useCategories();
  return useMemo(() => ({
    category: localizeCategoryOrBlank(cats, category, language).trim(),
    name: productName.value.trim(),
  }), [cats, category, language, productName.value]);
}

// WHAT THE FINISHED PIECE IS CALLED — "Chairs Bergen": the category and the product name, as the
// info table prints them. Both may be empty on a manual nobody has read yet, so it falls back to
// what it is.
export function useProductLabel(docId: string): string {
  const { t } = useLanguage();
  const product = useProductIdentity(docId);
  return [product.category, product.name].filter(Boolean).join(' ') || t('notice.models.wholeProduct');
}

// Where a cell of the product column has a value and, under it, what says where it came from.
export type ProductCell = { value: string; under?: ReactNode };

// THE WHOLE PRODUCT, AS THE PARTS TABLE'S FIRST COLUMN — the rows a product can answer.
//
// The column was already the product's in one row: its listing-crops cell is every part's
// boxes on the product's photographs. The rest of it was blank, and the product had facts to
// put there — above all its overall size, which the size read has taken off the dimension
// drawing since the product's mesh needed one, and which nothing on this page showed.
//
// ONLY WHAT IS THE PRODUCT'S OWN. Its category and name, the submodel it is in, and the size
// the drawing prints for it. The rows about a part in a box — found in the database,
// quantity, symmetry, letter, supplied, the manual's drawing of it — stay blank rather than
// borrowing a value: a chair is not a Parts-DB row and does not come in twos.
//
// DRAWING-ONLY SIZE. The estimate measures a part against parts already measured; a product
// has no such row to be estimated against here, so its size is what the drawing printed or
// nothing.
export function useProductColumn(docId: string, sizing: boolean) {
  const { t } = useLanguage();
  const { category, name } = useProductIdentity(docId);
  const submodels = useSubmodels(docId);
  const submodel = useSubmodelFilter(docId);

  // The submodel's materials, in the dropdown's words WITHOUT its number: the number tells
  // the dropdown's lines apart, and a cell holding one submodel has nothing to tell apart. A
  // product is made of what its submodel is made of; the per-part menus choose among those.
  const at = submodels.findIndex((s) => s.id === submodel);
  const material = at >= 0
    ? displayVariantLabel(variantLabel(submodels[at])) || t('notice.listing.label')
    : '';

  // Read on every render, like the size badge's thinking: the read that writes it is the
  // phase's, and the table repaints when that read lands on the parts.
  const size = readProductSize(docId);

  return {
    category: {
      value: category,
      // THAT THIS COLUMN IS NOT A PART, said at its top. Its header row is a category like
      // every other column's, and "CHAIR" over the product reads as one more chair piece.
      under: (
        <span className="mt-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {t('notice.models.wholeProduct')}
        </span>
      ),
    } satisfies ProductCell,
    name,
    material,
    size: {
      // The same three lines a component's cell has, in the same words, so the product's
      // height can be read straight across against its parts'.
      value: sizing ? '…' : (['x', 'y', 'z'] as const)
        .map((k) => `${sizeKeyLabel(k)}: ${formatMeasure(size, k)}`)
        .join('\n'),
      // What was read, in the model's words — "73,3 cm overall" is how to check a number.
      under: !sizing && size?.note ? (
        <span className="mt-1 block whitespace-normal font-sans text-[10px] text-slate-400">
          {size.note}
        </span>
      ) : undefined,
    } satisfies ProductCell,
  };
}
